import { observable, computed } from 'knockout-decorators'
import * as ko from 'knockout'
import type {
  CartRequest,
  CartResponse,
  TouristTaxGroupedRequest,
  TouristTaxGuest,
} from '@tixa/schema'
import { CartStatus, Gender } from '@tixa/schema'
import { PageState } from '~/store/page'
import { EventState } from '~/store/event'
import { CartState } from './index'
import { Analytics, Language, isMinorByEventStartDate } from '~/utils'
import * as Yup from 'yup'
import { isValidHungarianPostalCode } from '~/utils/postalCodes'

import { useCartTimeStorageTimer } from './store-cart-time'
import { useCartExpiryCheckTimer } from './expiry-checker'
export interface TouristTaxGuestData {
  name: string
  dateOfBirth?: Date
  gender?: Gender
  citizenship: string
  country: string
  postalCode: string
  isTaxFree?: boolean
  selectedCountryId?: number
}

export interface TouristTaxContent {
  id: number
  ticketTypeId: number
  seatmapSeatId?: string
  quantityMin: number
  quantityMax: number
  guests: ko.ObservableArray<TouristTaxGuestData>
}

export class TouristTaxState {
  public static get(): TouristTaxState {
    if (!TouristTaxState.instance) {
      TouristTaxState.instance = new TouristTaxState()
    }
    return TouristTaxState.instance
  }

  private static instance: TouristTaxState
  private pageState = PageState.get()
  private analytics = Analytics.getInstance()
  private Locale = Language.getInstance()

  public touristTax = false

  @observable
  public content: TouristTaxContent[] = []

  @observable
  public touristTaxAmount = 0

  @observable
  public touristTaxCurrency = 'HUF'

  @observable
  public isSubmittingTouristTax = false

  private constructor() {
    this.touristTax = false
  }

  // Default country constants - single source of truth
  public static readonly DEFAULT_COUNTRY_ID = 348
  public static readonly DEFAULT_COUNTRY = {
    id: 348,
    alpha2: 'hu',
    alpha3: 'hun',
    name: 'Magyarország',
  }

  public static getInstance(): TouristTaxState {
    if (!TouristTaxState.instance) {
      TouristTaxState.instance = new TouristTaxState()
    }
    return TouristTaxState.instance
  }

  @observable public selectedCountryId = TouristTaxState.DEFAULT_COUNTRY_ID

  @observable
  public showTouristTaxForm = false

  @observable
  public request?: CartRequest = undefined

  @observable
  public isRequestOutdated = false

  public cartData?: CartRequest

  public cartResponse?: CartResponse

  public errorMessage(key: string): string {
    return this.Locale.translations[key]
  }

  @observable
  public hasErrors = false

  @observable
  public error: string | null = null

  @observable
  public submissionError: string | null = null

  @observable
  public submissionErrorMessage: string | null = null

  @observable
  public queueResponse: CartResponse | null = null

  private namePattern = /^[\p{L}\p{N}\s\-'.]+$/u
  private postalCodePattern = /^[\p{L}\p{N}\s-]+$/u

  private guestValidationSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, () => this.errorMessage('touristTaxFormInputLength'))
      .max(256, () => this.errorMessage('touristTaxFormInputTooLong'))
      .matches(this.namePattern, () =>
        this.errorMessage('touristTaxFormInvalidCharacters')
      )
      .required(() => this.errorMessage('touristTaxFormName')),
    dateOfBirth: Yup.date()
      .required(() => this.errorMessage('touristTaxFormDateOfBirth'))
      .max(new Date(), () => this.errorMessage('touristTaxFormDateFuture'))
      .min(
        new Date(new Date().setFullYear(new Date().getFullYear() - 150)),
        () => this.errorMessage('touristTaxFormDateTooOld')
      ),
    isTaxFree: Yup.boolean().when('dateOfBirth', {
      is: (dateOfBirth: Date) => {
        if (!dateOfBirth) return false
        return isMinorByEventStartDate(
          EventState.get().event?.startDate || new Date(),
          dateOfBirth
        )
      },
      then: (schema) =>
        schema.oneOf([true], () =>
          this.errorMessage('touristTaxFormIsTaxable')
        ),
      otherwise: (schema) => schema,
    }),
    gender: Yup.string()
      .oneOf(['male', 'female'], () =>
        this.errorMessage('touristTaxFormGenderInvalid')
      )
      .required(() => this.errorMessage('touristTaxFormGender')),
    citizenship: Yup.string()
      .max(64, () => this.errorMessage('touristTaxFormInputTooLong'))
      .matches(this.namePattern, () =>
        this.errorMessage('touristTaxFormInvalidCharacters')
      )
      .required(() => this.errorMessage('touristTaxFormCitizenship')),
    country: Yup.string()
      .max(128, () => this.errorMessage('touristTaxFormInputTooLong'))
      .matches(this.namePattern, () =>
        this.errorMessage('touristTaxFormInvalidCharacters')
      )
      .required(() => this.errorMessage('touristTaxFormCountry')),
    postalCode: Yup.string().when('selectedCountryId', {
      is: TouristTaxState.DEFAULT_COUNTRY_ID,
      then: (schema) =>
        schema
          .min(4, () => this.errorMessage('touristTaxFormPostalCodeLength'))
          .max(4, () => this.errorMessage('touristTaxFormPostalCodeLength'))
          .test(
            'valid-hungarian-zip',
            () => this.errorMessage('touristTaxFormPostalCodeInvalid'),
            (value) => {
              if (!value) return false
              return isValidHungarianPostalCode(value)
            }
          )
          .required(() => this.errorMessage('touristTaxFormPostalCode')),
      otherwise: (schema) =>
        schema
          .min(3, () => this.errorMessage('touristTaxFormInputLength'))
          .max(16, () => this.errorMessage('touristTaxFormInputTooLong'))
          .matches(this.postalCodePattern, () =>
            this.errorMessage('touristTaxFormInvalidCharacters')
          )
          .required(() => this.errorMessage('touristTaxFormPostalCode')),
    }),
    selectedCountryId: Yup.number(),
  })

  private getValidationSchema() {
    return Yup.array().of(this.guestValidationSchema).min(1)
  }

  public async showTouristTaxFormModal(cartData?: CartRequest) {
    this.cartData = cartData
    this.error = null
    this.submissionError = null
    this.submissionErrorMessage = null
    this.remainingTouristTaxTime = 0
    const success = await this.initializeGuests()
    if (success) {
      this.showTouristTaxForm = true
    }
  }

  public async hideTouristTaxFormModal() {
    // Stop timers when hiding the modal
    await this.touristTaxTimeStorageTimer.stop()
    await this.touristTaxExpiryTimer.stop()

    const cartState = CartState.get()
    const shouldDeleteCart =
      this.cartResponse?.id && this.cartResponse.id !== cartState.content?.id

    if (shouldDeleteCart) {
      await this.deleteCart()
    }

    if (shouldDeleteCart) {
      EventState.get().cartRequest = null
    }

    this.cartData = undefined
    this.cartResponse = undefined
    this.content = []
    this.request = undefined
    this.isRequestOutdated = false
    this.touristTaxAmount = 0
    this.touristTaxCurrency = 'HUF'
    this.selectedCountryId = TouristTaxState.DEFAULT_COUNTRY_ID
    this.showTouristTaxForm = false
  }

  private async initializeGuests(): Promise<boolean> {
    try {
      const result = await this.pageState.api.cart.requestCart(
        this.cartData || { countedTickets: [], reservations: [] }
      )

      if (!result.status) {
        console.error('Server error during tourist tax initialization:', result)
        this.error = 'error'
        this.submissionError = 'error'
        this.submissionErrorMessage = this.errorMessage('serverError')
        return false
      }

      if (
        result.status === CartStatus.wait ||
        result.status === CartStatus.queue
      ) {
        this.queueResponse = result
        return true
      }

      const errorStates: CartStatus[] = [
        CartStatus.couponused,
        CartStatus.error,
        CartStatus.onecart,
        CartStatus.soldout,
      ]

      if (errorStates.includes(result.status)) {
        this.updateErrorResponse(result)
        CartState.get().updateFromResponse(result)
        return false
      }

      this.processCartResponse(result)
      return true
    } catch (e) {
      console.error('Failed to fetch tourist tax data:', e)
      this.error = 'error'
      this.submissionError = 'error'
      this.submissionErrorMessage = this.errorMessage('serverError')
      this.content = []
      this.touristTaxAmount = 0
      this.touristTaxCurrency = 'HUF'
      return false
    }
  }

  private async processCartResponse(result: CartResponse) {
    this.cartResponse = result
    this.queueResponse = null

    const offersWithTouristTax = result.cart.filter(
      (offer) => offer.touristTaxQuantity
    )

    let contentId = 1

    this.content = offersWithTouristTax.flatMap((offer) => {
      const isSeatedTicket = offer.seatmapSeatId && offer.seatmapSeatId !== '0'
      const quantity = isSeatedTicket ? 1 : offer.eligibleQuantity?.value || 1

      return Array.from({ length: quantity }, () => ({
        id: contentId++,
        ticketTypeId: parseInt(offer.sku) || contentId,
        seatmapSeatId: offer.seatmapSeatId,
        quantityMin: offer.touristTaxQuantity?.min || 1,
        quantityMax: offer.touristTaxQuantity?.max || 1,
        guests: this.createInitialGuests(offer.touristTaxQuantity?.min || 1),
      }))
    })

    if (result.touristTaxAmount) {
      const taxedNightCount = result.touristTaxAmount?.taxedNightCount || 0
      const pricePerNight = result.touristTaxAmount?.pricePerNight || 0
      this.touristTaxAmount = pricePerNight * taxedNightCount
    }

    this.touristTaxCurrency = result.cart[0]?.priceCurrency || 'HUF'

    // Start timers when cart response is successfully processed
    if (result?.id && result.status === CartStatus.cart) {
      await this.touristTaxTimeStorageTimer.start()
      this.remainingTouristTaxTime = await this.touristTaxExpiryTimer.start(
        result?.expiry
      )
    }
  }

  private queueFinished(cart: CartResponse) {
    this.processCartResponse(cart)
  }

  private async queueError(cart: CartResponse) {
    console.error('Queue error:', cart)
    this.queueResponse = null
    this.error = 'error'
    this.submissionError = 'error'
    this.submissionErrorMessage = cart.message || 'Queue error occurred'
    await this.hideTouristTaxFormModal()
  }

  private createInitialGuests(
    quantityMin: number
  ): ko.ObservableArray<TouristTaxGuestData> {
    const guests = Array.from({ length: quantityMin }, () =>
      this.createEmptyGuestData()
    )
    return ko.observableArray(guests)
  }

  private createEmptyGuestData(): TouristTaxGuestData {
    return {
      name: '',
      dateOfBirth: undefined,
      gender: undefined,
      citizenship: '',
      country: '',
      postalCode: '',
      isTaxFree: false,
      selectedCountryId: TouristTaxState.DEFAULT_COUNTRY_ID,
    }
  }

  public addGuest(id: number) {
    const guest = this.content.find((g) => g.id === id)
    if (guest && guest.guests().length < guest.quantityMax) {
      guest.guests.push(this.createEmptyGuestData())
    }
  }

  public removeGuest(id: number) {
    const guest = this.content.find((g) => g.id === id)
    if (guest && guest.guests().length > guest.quantityMin) {
      guest.guests.pop()
    }
  }

  public removeSpecificGuest(ticketTypeId: number, guestIndex: number) {
    const guest = this.content.find((g) => g.ticketTypeId === ticketTypeId)
    if (
      guest &&
      guest.guests().length > guest.quantityMin &&
      guestIndex >= 0 &&
      guestIndex < guest.guests().length
    ) {
      guest.guests.splice(guestIndex, 1)
    }
  }

  public canAddGuest(id: number): boolean {
    const guest = this.content.find((g) => g.id === id)
    return guest ? guest.guests().length < guest.quantityMax : false
  }

  public canRemoveGuest(id: number): boolean {
    const guest = this.content.find((g) => g.id === id)
    return guest ? guest.guests().length > guest.quantityMin : false
  }

  public canRemoveSpecificGuest(ticketTypeId: number): boolean {
    const guest = this.content.find((g) => g.ticketTypeId === ticketTypeId)
    return guest ? guest.guests().length > guest.quantityMin : false
  }

  public removeSpecificGuestById(contentId: number, guestIndex: number) {
    const guest = this.content.find((g) => g.id === contentId)
    if (
      guest &&
      guest.guests().length > guest.quantityMin &&
      guestIndex >= 0 &&
      guestIndex < guest.guests().length
    ) {
      guest.guests.splice(guestIndex, 1)
    }
  }

  public canRemoveSpecificGuestById(contentId: number): boolean {
    const guest = this.content.find((g) => g.id === contentId)
    return guest ? guest.guests().length > guest.quantityMin : false
  }

  public getTotalGuests(): number {
    return this.content.reduce(
      (total, guest) => total + guest.guests().length,
      0
    )
  }

  public notifyGuestsChanged() {
    this.content.forEach((contentItem) => {
      contentItem.guests.valueHasMutated()
    })
  }

  @computed
  public get getTaxableGuestsCount(): number {
    return this.content.reduce((total, guest) => {
      return total + guest.guests().filter((guest) => !guest.isTaxFree).length
    }, 0)
  }

  @computed
  public get getTotalTouristTax(): number {
    return this.getTaxableGuestsCount * this.touristTaxAmount
  }

  public getCartData(): CartRequest | undefined {
    return this.cartData
  }

  public async deleteCart() {
    if (this.cartResponse?.id) {
      try {
        await this.pageState.api.waitingRoom.delete(this.cartResponse.id)
      } catch (e) {
        console.debug('Failed to delete tourist tax cart:', e)
      }
    }
  }

  public async clear() {
    console.debug('Clearing tourist tax state')
    // Stop timers when clearing the state
    await this.touristTaxTimeStorageTimer.stop()
    await this.touristTaxExpiryTimer.stop()

    // Delete the cart to free up reserved seats
    if (this.cartResponse?.id) {
      await this.deleteCart()
    }

    EventState.get().cartRequest = null

    this.showTouristTaxForm = false
    this.cartData = undefined
    this.cartResponse = undefined
    this.content = []
    this.request = undefined
    this.error = null
    this.submissionError = null
    this.submissionErrorMessage = null
    this.queueResponse = null
  }

  private touristTaxTimeStorageTimer = useCartTimeStorageTimer()
  private touristTaxExpiryTimer = useCartExpiryCheckTimer(
    () => this.isInQueue,
    async () => {
      if (this.analytics.checkoutExpired) {
        this.analytics.checkoutExpired()
      }
      await this.clear()
    },
    (remaining) => {
      this.remainingTouristTaxTime = remaining
    }
  )

  @observable remainingTouristTaxTime = 0

  @computed
  public get hasTouristTaxExpired(): boolean {
    return this.remainingTouristTaxTime < 0
  }

  @computed
  public get isInQueue(): boolean {
    return (
      this.queueResponse?.status === CartStatus.wait ||
      this.queueResponse?.status === CartStatus.queue
    )
  }

  @computed
  public get queue() {
    if (!this.isInQueue) {
      return null
    }
    return {
      cartId: this.queueResponse?.id,
      finished: (cart: CartResponse) => {
        console.debug('Tourist Tax Queue finished')
        this.queueFinished(cart)
      },
      error: (cart: CartResponse) => {
        console.debug('Tourist Tax Queue error')
        this.queueError(cart)
      },
    }
  }

  public updateErrorResponse(response: CartResponse | null) {
    if (!response) {
      this.error = null
      this.submissionError = null
      this.submissionErrorMessage = null
      return
    }

    if (response.status === 'error') {
      this.error = 'error'
      this.submissionError = 'error'
      this.submissionErrorMessage =
        response.message || (response as any).error || null
    }
  }

  public async submit(e?: Event) {
    if (e) e.preventDefault()
    if (this.isSubmittingTouristTax) return
    this.isSubmittingTouristTax = true

    const isValid = await this.validateTouristTaxForm()

    if (isValid) {
      // Build grouped structure
      const guestGroups: TouristTaxGuest[][] = []

      this.content.forEach((contentItem) => {
        const groupGuests: TouristTaxGuest[] = []
        contentItem.guests().forEach((guest) => {
          groupGuests.push({
            ticketTypeId: contentItem.ticketTypeId,
            name: guest.name,
            dateOfBirth: guest.dateOfBirth || new Date(),
            gender: guest.gender || Gender.MALE,
            citizenship: guest.citizenship,
            country: guest.country,
            postalCode: guest.postalCode,
            touristTaxPricePerNight:
              this.cartResponse?.touristTaxAmount?.pricePerNight || 0,
            taxedNightCount: guest.isTaxFree
              ? 0
              : this.cartResponse?.touristTaxAmount?.taxedNightCount || 0,
          })
        })
        if (groupGuests.length > 0) {
          guestGroups.push(groupGuests)
        }
      })

      const touristTaxData: TouristTaxGroupedRequest = {
        cartId: this.cartResponse?.id || 0,
        eventId: parseInt(
          this.cartData?.reservations[0]?.rendezvenyid ||
            this.cartData?.countedTickets[0]?.rendezvenyid ||
            '0'
        ),
        guestGroups: guestGroups,
      }

      this.error = null
      this.submissionError = null

      try {
        const response = await this.pageState.api.cart.addTouristTax(
          touristTaxData
        )

        if (response.status === 'cart') {
          this.isSubmittingTouristTax = false
          // Stop timers when submission succeeds
          await this.touristTaxTimeStorageTimer.stop()
          await this.touristTaxExpiryTimer.stop()
          CartState.get().updateFromResponse(response)
          await this.hideTouristTaxFormModal()
          return true
        } else if (response.status === 'error') {
          this.isSubmittingTouristTax = false
          // Stop timers when submission fails
          await this.touristTaxTimeStorageTimer.stop()
          await this.touristTaxExpiryTimer.stop()
          this.updateErrorResponse(response)
          await this.hideTouristTaxFormModal()
          return false
        }
      } catch (error) {
        console.error('Failed to submit tourist tax data:', error)
        this.error = 'error'
        this.submissionError = 'error'
        this.isSubmittingTouristTax = false
        // Stop timers on exception
        await this.touristTaxTimeStorageTimer.stop()
        await this.touristTaxExpiryTimer.stop()
        return false
      }
    }
    this.isSubmittingTouristTax = false
    return false
  }

  private async validateTouristTaxForm(): Promise<boolean> {
    try {
      this.clearValidationErrors()
      this.hasErrors = false

      const allGuests: TouristTaxGuestData[] = []
      const indexMapping: Array<{ contentIndex: number; guestIndex: number }> =
        []

      this.content.forEach((contentItem, contentIndex) => {
        contentItem.guests().forEach((guest, guestIndex) => {
          allGuests.push(guest)
          indexMapping.push({ contentIndex, guestIndex })
        })
      })

      const schema = this.getValidationSchema()
      await schema.validate(allGuests, {
        abortEarly: false,
      })

      return true
    } catch (err) {
      this.hasErrors = true

      const validationError = err as Yup.ValidationError
      const indexMapping: Array<{ contentIndex: number; guestIndex: number }> =
        []
      this.content.forEach((contentItem, contentIndex) => {
        contentItem.guests().forEach((guest, guestIndex) => {
          indexMapping.push({ contentIndex, guestIndex })
        })
      })

      validationError.inner.forEach((error: Yup.ValidationError) => {
        if (!error.path) return

        const match = error.path.match(/\[(\d+)\]\.(.+)/)
        if (match) {
          const globalGuestIndex = parseInt(match[1])
          const fieldName = match[2]

          const fieldId = `${fieldName}-${globalGuestIndex}`

          const errorMessageElement = document.querySelector(
            `[data-for="${fieldId}"]`
          ) as HTMLElement

          if (errorMessageElement) {
            errorMessageElement.textContent = error.message
            errorMessageElement.classList.add('visible')

            const inputElement = document.querySelector(`#${fieldId}`)
            if (inputElement) {
              inputElement.classList.add('is-invalid')
            }
          }
        }
      })

      if (this.analytics.formInvalid) {
        this.analytics.formInvalid()
      }

      return false
    }
  }

  public clearFieldError(fieldId: string) {
    const errorMessageElement = document.querySelector(
      `[data-for="${fieldId}"]`
    ) as HTMLElement

    if (errorMessageElement) {
      errorMessageElement.textContent = ''
      errorMessageElement.classList.remove('visible')
    }

    const inputElement = document.querySelector(`#${fieldId}`)
    if (inputElement) {
      inputElement.classList.remove('is-invalid')
    }
  }

  private clearValidationErrors() {
    const errorElements = document.querySelectorAll('.error-message.visible')
    errorElements.forEach((el) => {
      el.textContent = ''
      el.classList.remove('visible')
    })

    const invalidInputs = document.querySelectorAll('.is-invalid')
    invalidInputs.forEach((el) => {
      el.classList.remove('is-invalid')
    })
  }
}
